import {
  CHAT_TIERS,
  CHAT_TIER_LABELS,
  DEFAULT_CHAT_TIER,
  allowedChatTiers,
  clampChatTier,
  isChatTier,
  type ChatTier,
} from '~~/shared/chat/tiers'
import { readPref, writePref } from '~/composables/usePersistedPref'
import { isFreeTierProfile } from '~~/shared/organizations/freeTrial'

export type ModelId = ChatTier

/** All tiers, in display order. The selector shows a subset gated by the org's `max_chat_tier`. */
export const MODEL_OPTIONS: { id: ModelId; label: string }[] = CHAT_TIERS.map(id => ({
  id,
  label: CHAT_TIER_LABELS[id],
}))

/**
 * The ceiling for a signed-in user with NO organization (`/chat`, MAR-1390): Slow and Fast. A ceiling
 * rather than a list, because `allowedChatTiers('fast')` is exactly `['slow', 'fast']`.
 */
const ORGLESS_MAX_TIER: ChatTier = 'fast'

const STORAGE_KEY = 'marcus-model'
/** The `useState` key of the selection — `useAuth().signOut()` resets it to the default (MAR-1458). */
export const MODEL_SELECTOR_STATE_KEY = 'model-selector'

/**
 * Client-only, once per page load. Module scope is safe here precisely because it is never written on
 * the server — a request-shared flag would leak one request's restore into the next.
 */
let restored = false

export function useModelSelector() {
  const { profile } = useUserProfile()

  // The ceiling on selectable tiers (the server enforces it too — this only shapes the UI and the value
  // a turn sends). An org's `max_chat_tier`; Fast for a loaded profile with no organization; none while
  // the profile is still loading, so an org user's stored Pro is not briefly clamped to Fast.
  const maxTier = computed<ChatTier | null>(() => {
    const p = profile.value
    // A free-trial member (MAR-1452) gets the free ceiling too, whatever their trial organization's own setting.
    if (isFreeTierProfile(p)) return ORGLESS_MAX_TIER
    return isChatTier(p?.organization_max_chat_tier) ? p!.organization_max_chat_tier as ChatTier : null
  })
  const options = computed(() =>
    allowedChatTiers(maxTier.value).map(id => ({ id, label: CHAT_TIER_LABELS[id] })),
  )

  // The initializer runs on the SERVER, so it can never read `localStorage`: its value is serialized
  // into the Nuxt payload and `useState` reuses that on hydration without re-running the initializer.
  // Reading storage from in here therefore looked like it worked and silently did nothing — every
  // reload dropped the user back to Fast (AlexAmin/marcus#228). The restore happens client-side below.
  const selected = useState<ModelId>(MODEL_SELECTOR_STATE_KEY, () => DEFAULT_CHAT_TIER)

  // The tier in force is DERIVED: the selection, clamped to the ceiling. It used to be a watcher on
  // the ceiling that rewrote the selection after the fact (`apps/web/AGENTS.md`, "Vue reactivity").
  // Derived, a profile that loads late, an org that is restricted, or a stored tier above the ceiling
  // can never be read unclamped — there is no window between the change and the reaction to it.
  const modelId = computed<ModelId>({
    get: () => clampChatTier(selected.value, maxTier.value),
    set: id => setModel(id),
  })

  // Deferred to after hydration: setting it synchronously would make the client's first render
  // disagree with the server's markup.
  if (import.meta.client && !restored) {
    restored = true
    nextTick(() => {
      const stored = readPref(STORAGE_KEY)
      if (isChatTier(stored)) setModel(stored)
    })
  }

  function setModel(id: ModelId) {
    const clamped = clampChatTier(id, maxTier.value)
    selected.value = clamped
    if (import.meta.client) writePref(STORAGE_KEY, clamped)
  }

  return { modelId, setModel, options }
}
